import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { LANDING } from "@/constants/testIds";
import { toast } from "@/components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/waitlist`, { email: email.trim() });
      if (res.data?.message === "already_joined") {
        toast.info("You're already on the list — hang tight!");
      } else {
        toast.success("You're in! We'll ping you when GeoBeats goes live.");
      }
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="waitlist"
      className="relative bg-[#07030f] py-28 sm:py-36 overflow-hidden border-t border-white/[0.06]"
    >
      <motion.div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-[#a259ff] opacity-[0.12] blur-[140px]"
        animate={{ scale: [1, 1.15, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="relative z-10 max-w-2xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#22d3ee] font-semibold">
            Early access
          </span>
          <h2
            data-testid={LANDING.waitlistHeading}
            className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 mb-5 leading-tight"
          >
            Be first on the map.
          </h2>
          <p className="text-[#b9a9e6] max-w-md mx-auto mb-10">
            GeoBeats is launching soon. Drop your email and we'll invite you
            in before anyone else.
          </p>

          <form
            data-testid={LANDING.waitlistForm}
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto"
          >
            <input
              data-testid={LANDING.waitlistEmailInput}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full rounded-full bg-white/5 border border-white/15 px-6 py-3.5 text-sm text-white placeholder:text-[#7c6a9e] focus:outline-none focus:border-[#22d3ee]/60 focus:shadow-[0_0_20px_rgba(34,211,238,0.25)] transition-all duration-300"
            />
            <button
              data-testid={LANDING.waitlistSubmitButton}
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto shrink-0 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#a259ff] to-[#7c3aff] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_30px_rgba(162,89,255,0.45)] hover:shadow-[0_0_45px_rgba(162,89,255,0.7)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Join waitlist <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
